/**
 * TextField component
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import {
	BaseSyntheticEvent,
	ChangeEvent,
	DetailedHTMLProps,
	InputHTMLAttributes,
	KeyboardEvent,
	useEffect,
	useId,
	useMemo,
	useState,
} from 'react';
import { Text } from '@components';
import { WarningQld } from '@public';

import { FieldWidth } from '..';

// CSS Style for this module
import styles from './textField.module.scss';

type HTMLInputMode =
	| 'text'
	| 'search'
	| 'email'
	| 'tel'
	| 'url'
	| 'none'
	| 'numeric'
	| 'decimal'
	| undefined;

type AccessibilityProps = Pick<
	DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
	'id' | 'name' | 'required'
>;

/**
 * Interface for TextField props
 */
interface TextFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * variant of the text field
	 */
	variant?: 'filled' | 'outline';
	/**
	 * preset type for the html input tag
	 */
	type?: string;
	/**
	 * inputMode for the html input tag
	 */
	inputMode?: HTMLInputMode;
	/**
	 * label of the input element when there is no external label
	 */
	label?: string;
	/**
	 * Default text that is displayed within the field
	 */
	placeholder?: string;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Helper text wrapped by HTML elements for additional guidance about how the input field will be used.
	 */
	hintChildren?: React.ReactNode;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Number for determining the text limit input
	 */
	maxLength?: number;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	/**
	 * triggered if the user presses enter key for submitting a value.
	 * debounce is built in
	 */
	handleSubmit?: () => void;
	/**
	 * default value for edit mode
	 */
	defaultValue?: string;
	/**
	 * Pattern as regular expression of valid characters in the field value
	 */
	pattern?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Boolean for determining whether the enter key should trigger the submit handler.
	 */
	submitOnEnterKeyDown?: boolean;
	/**
	 * Width size of the input element.
	 * Accepts 'small', 'medium', 'large' or 'full' as fluid width.
	 * Or 'fixedWidth1' to 'fixedWidth6' for a standardized fixed width.
	 */
	size?: FieldWidth;
	/**
	 * Additional props to be passed to the underlying input element for accessibility purposes.
	 * @see https://reactjs.org/docs/accessibility.html#programmatic-access-to-event-handlers
	 */
	accessibilityProps?: AccessibilityProps;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/** Element identifier selector for test */
	testId?: string;
	/**
	 * Function to be called when the input loses focus
	 */
	onBlur?: () => void;
}

/**
 * Text fields let users enter and edit text.
 * @param {TextFieldProps} Prop
 * @returns an styled input element
 */
export const TextField = ({
	customStyles,
	variant = 'outline',
	type = 'text',
	inputMode,
	label = '',
	placeholder,
	hintText,
	hintChildren,
	errorMsg,
	maxLength,
	handleChange = () => {
		return;
	},
	handleSubmit = () => {
		return;
	},
	defaultValue = '',
	pattern,
	disabled = false,
	size = 'full',
	submitOnEnterKeyDown = false,
	autoFocus = false,
	accessibilityProps,
	testId,
	onBlur,
}: TextFieldProps) => {
	const TEXTAREA = 'textarea';
	const [value, setValue] = useState(defaultValue);
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);

	useEffect(() => {
		setValue(defaultValue);
	}, [defaultValue]);

	const evaluateValue = (event: BaseSyntheticEvent) => {
		const isValid = pattern ? RegExp(pattern).test(event.target.value) : true;
		if (isValid) {
			setValue(event.target.value);
		}
		return isValid;
	};

	const onType = (event: BaseSyntheticEvent) => {
		if (evaluateValue(event)) {
			handleChange(event as ChangeEvent<HTMLInputElement>);
		}
	};

	const trim = (event: BaseSyntheticEvent) => {
		if (event?.target && event.target.value != undefined) {
			event.target.value = event.target.value?.trim();
			onType(event);
		}
	};

	const handleKeyDown = (e: KeyboardEvent) => {
		// only triggered by the enter key
		if (e.key === 'Enter') {
			if (submitOnEnterKeyDown) {
				handleSubmit();
			} else {
				e.preventDefault();
			}
		}
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<div className={styles.textContainer}>
				{label && (
					<label htmlFor={label}>
						<Text variant="label">{label}</Text>
					</label>
				)}
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
						{hintChildren && (
							<div className={styles.hint} id={`${hintId}-additional`}>
								{hintChildren}
							</div>
						)}
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			{type === TEXTAREA ? (
				<textarea
					className={[
						styles[`field-width--${size}`],
						styles.textArea,
						styles[`${variant}`],
						styles[`textArea-disabled-${disabled}`],
						hasError && styles.error,
					].join(' ')}
					value={value}
					onChange={onType}
					onBlur={e => {
						trim(e);
						onBlur?.();
					}}
					placeholder={placeholder}
					disabled={disabled}
					// Props for accessibility
					id={accessibilityProps?.id}
					name={accessibilityProps?.name}
					required={accessibilityProps?.required}
					aria-required={accessibilityProps?.required}
					aria-label={label && label.length ? label : undefined} // when there is no external <label> element
					aria-describedby={hintId || errorId ? [hintId, errorId].join(' ') : undefined}
					autoFocus={autoFocus}
					// Testing
					data-testid={testId}
				/>
			) : (
				<input
					className={[
						styles[`field-width--${size}`],
						styles.textField,
						styles[`${variant}`],
						styles[`textField-disabled-${disabled}`],
						hasError && styles.error,
					].join(' ')}
					value={value}
					onChange={onType}
					onBlur={e => {
						trim(e);
						onBlur?.();
					}}
					onKeyDown={handleKeyDown}
					type={type}
					inputMode={inputMode}
					placeholder={placeholder}
					maxLength={maxLength ? maxLength : undefined}
					disabled={disabled}
					pattern={pattern}
					// Props for accessibility
					id={accessibilityProps?.id}
					name={accessibilityProps?.name}
					required={accessibilityProps?.required}
					aria-required={accessibilityProps?.required}
					aria-label={label && label.length ? label : undefined}
					aria-describedby={hintId || errorId ? [hintId, errorId].join(' ') : undefined}
					autoFocus={autoFocus}
					// Testing
					data-testid={testId}
				/>
			)}
		</div>
	);
};
